/**
 * The durable end of a recording: activation, and the final watermark (#170-B).
 *
 * #170-A made every claim delta durable. It deliberately left *finality* in memory: the
 * outbox's `writerAck` says what this device believes landed, and an in-memory value is
 * not a promise that a device finished. This module turns that belief into a row the
 * server's stop barrier can act on, and it owns the ordering that makes the row true.
 *
 * ## Why this is not in the outbox
 *
 * The outbox is a sequence writer. It allocates, signs, sends, and reconciles -- and it
 * is the sole owner of the ACK. Putting the marker inside it would hide the session's
 * completion contract inside append retries, where a lease refresh and a sequence
 * reconciliation already share a code path. The marker has a different failure algebra:
 * it is idempotent on a primary key rather than on a payload digest, an exact retry is
 * the *designed* outcome of a timeout rather than a recovery, and a refusal ends the
 * recording rather than a queue item. One owner each.
 *
 * ## The one rule
 *
 * **The body is built once and never rebuilt.** The server accepts a marker only if its
 * `(sequence, digest)` equals the committed ledger head exactly, so the pair is not a
 * request -- it is a claim about a fact the server can check. Recomputing it between
 * attempts is what turns a recoverable timeout into an unrecoverable disagreement: the
 * first request may have committed, and a second body describing a different pair can
 * never be reconciled against it. Only the three producer-lease fields may change, and
 * only because they ride outside the pair the server compares.
 *
 * ## Why `duplicate` is success
 *
 * A lost response is indistinguishable from a request that never arrived, and the only
 * safe move is to resend the identical body. The route answers an exact replay
 * `duplicate` -- ahead of every rejection, including after a producer takeover and after
 * the session has moved to `finalising`. Treating that as a failure would strand a device
 * whose proof is already durable: it would report incomplete, decline to call leave, and
 * the whole table would wait out the close grace period for a device that had finished.
 *
 * ## What a refusal is not
 *
 * It is not permission to fall back. Once activation is durable this device is counted by
 * the barrier for the rest of the session, and a legacy claim bundle cannot discharge
 * that -- the two are different signed formats over different evidence. A marker this
 * device cannot publish leaves the session visibly incomplete, which is the honest
 * outcome and the one the server is built to degrade explicitly.
 *
 * Everything is injected, so every uncertainty and reconnect case is testable without a
 * network, a DOM, or a clock.
 */

import type { ClaimDeltaContext } from "./claim-delta-outbox";
import type {
  ClaimDeltaActivateRequest,
  ClaimDeltaActivateResponse,
  ClaimDeltaFinalWatermarkRequest,
  ClaimDeltaFinalWatermarkResponse,
} from "../contract/api";

/** The exact writer ACK pair a marker publishes. Sequence 0 carries a null digest. */
export interface WriterAck {
  sequence: number;
  digest: string | null;
}

/**
 * The wire body. Built once; only the three lease fields are ever replaced.
 *
 * Exactly these six keys and no others [V]. The server's request models are
 * `extra="forbid"`, so a body carrying the outbox's `fence`/`session_generation` spelling
 * alongside `producer_fence`/`producer_generation` is a 422 -- and a 422 on a marker is
 * terminal, so the mistake would present as a session that can never report complete
 * rather than as an obvious client bug.
 */
export type FinalWatermarkBody = ClaimDeltaFinalWatermarkRequest;

export type ActivateBody = ClaimDeltaActivateRequest;

/**
 * A 2xx from either route. Both `accepted` and `duplicate` are success.
 *
 * `data` is a partial view of both generated marker responses, because one wrapper is
 * shared by the two routes and neither answer is trusted beyond the fields read here.
 */
export interface MarkerResponse {
  status: number;
  data?: Partial<
    ClaimDeltaFinalWatermarkResponse & ClaimDeltaActivateResponse
  > | null;
}

export type MarkerOutcome = "accepted" | "duplicate";

/**
 * What an activation attempt established, and it is three answers rather than two.
 *
 * The middle one is the whole point (#170-B) [V]. `activate` is idempotent and commits
 * the durable barrier row *before* its response is written, so a timeout or a dropped
 * connection cannot distinguish "it never committed" from "it committed and the answer
 * was lost". A boolean forced that ambiguity into one of the two certainties, and the
 * one it chose was `false` -- so the browser selected the legacy bundle path for a
 * session the server may already have been counting, and the merge then waited for a
 * final watermark that by construction would never arrive.
 *
 * - `active`: the row exists. Delta mode, and no downgrade for the rest of the recording.
 * - `declined`: a *definitive* refusal reached before anything durable could exist -- the
 *   deployment does not offer the route, or this device has no accepted producer. Legacy
 *   is a genuine compatibility choice here, and only here.
 * - `uncertain`: the row may exist. Never legacy.
 */
export type ActivationOutcome = "active" | "declined" | "uncertain";

/**
 * Why a marker can never be published.
 *
 * Each name is a distinct server refusal, kept distinct because they mean different
 * things to a person reading a wrap-up screen and to telemetry. None of them is
 * recoverable by this device: not one may be repaired by deriving a new pair from the
 * server's reported `high_water`, which would be inventing finality for deltas this
 * device cannot identify.
 */
export type MarkerTerminalReason =
  | "final_watermark_stale"
  | "final_watermark_ahead"
  | "final_watermark_conflict"
  | "device_state_missing"
  | "session_closed"
  | "invalid_final_watermark"
  | "producer_lease_exhausted"
  | "drain_incomplete"
  | "marker_timeout"
  | "marker_refused";

export class MarkerTerminalError extends Error {
  readonly reason: MarkerTerminalReason;
  constructor(reason: MarkerTerminalReason, message: string) {
    super(message);
    this.name = "MarkerTerminalError";
    this.reason = reason;
  }
}

/**
 * 409 reasons that mean the *lease* is stale rather than the pair being wrong.
 *
 * Mirrors the append path's set, and for the same reason: the lease rides outside the
 * signature and outside the pair the server compares, so it is the one part of the body
 * that may be replaced without changing what is being claimed.
 */
const LEASE_REASONS = new Set([
  "producer_not_accepted",
  "producer_fence_conflict",
  "stale_fence",
  "future_fence",
  "stale_session_generation",
  "future_generation",
]);

/** 409/422 reasons that end the recording's claim finality. */
const TERMINAL_REASONS = new Set<string>([
  "final_watermark_stale",
  "final_watermark_ahead",
  "final_watermark_conflict",
  "device_state_missing",
  "session_closed",
  "invalid_final_watermark",
]);

function reasonOf(err: { data?: unknown }): string | null {
  const detail = (err?.data as { detail?: unknown } | undefined)?.detail;
  if (!detail || typeof detail !== "object") return null;
  const reason = (detail as { error?: unknown }).error;
  return typeof reason === "string" ? reason : null;
}

export type MarkerVerdict = "retry" | "refresh-lease" | "terminal" | "signed-out";

/**
 * Classify one failed marker attempt.
 *
 * Deliberately *not* `classifyClaimDeltaError`. That function's `uncertain` verdict sends
 * the append path to the `resume` handshake, which is exactly wrong here: `resume` reports
 * a high-water, and deriving a final pair from a high-water is inventing the fact the
 * marker exists to prove. For the marker, an uncertain answer is resolved by resending
 * the identical body -- the primary key makes that safe, and it is the only resolution
 * that cannot fabricate finality.
 */
export function classifyMarkerError(
  err: Error & { status?: number; data?: unknown }
): MarkerVerdict {
  const status = err?.status;
  if (status === 401) return "signed-out";
  // No status is a transport failure: the request may have been received, committed, and
  // only the answer lost. An exact resend resolves it as `duplicate`.
  if (typeof status !== "number") return "retry";
  if (status === 408 || status === 429 || status >= 500) return "retry";
  if (status === 409) {
    const reason = reasonOf(err);
    if (reason && LEASE_REASONS.has(reason)) return "refresh-lease";
    if (reason && TERMINAL_REASONS.has(reason)) return "terminal";
    // An unrecognised 409 is still a statement about a pair that is already fixed.
    return "terminal";
  }
  if (status === 422) return "terminal";
  if (status === 404) {
    // The capability went away underneath an activated session. Not a fallback: this
    // device is already counted by the barrier, and the server will degrade it.
    return "terminal";
  }
  if (status >= 400 && status < 500) return "terminal";
  return "retry";
}

export interface FinalWatermarkPorts {
  timelineEpoch: () => string;
  /** POST activate. Returns status + body; throws with `status`/`data` on failure. */
  activate: (
    body: ActivateBody,
    options: { signal: AbortSignal }
  ) => Promise<MarkerResponse>;
  /**
   * POST final-watermark. The body is transmitted verbatim.
   *
   * The signal is how an attempt is actually bounded rather than merely given up on: a
   * `fetch` into a black hole never settles, and a promise nobody can cancel holds the
   * wrap-up open for as long as the device stays on the network.
   */
  publish: (
    body: FinalWatermarkBody,
    options: { signal: AbortSignal }
  ) => Promise<MarkerResponse>;
  /** Re-read the producer lease. Only ever used to replace the three lease fields. */
  context: () => Promise<ClaimDeltaContext>;
  onSignedOut?: (error: Error) => void;
  /** Told once per state transition, for the wrap-up stage and telemetry. */
  onStage?: (stage: MarkerStage, detail?: string) => void;
  sleep?: (ms: number) => Promise<void>;
  /** Attempts before an uncertain marker is given up on. */
  maxAttempts?: number;
  /** Lease refreshes spent on one marker before giving up. */
  maxLeaseRefreshes?: number;
  /** Wall-clock bound on one publish attempt. */
  attemptDeadlineMs?: number;
  /** Wall-clock bound on the whole marker operation, across attempts. */
  totalDeadlineMs?: number;
  /** Injected so the deadline is testable without waiting. */
  now?: () => number;
}

export type MarkerStage =
  | "activating"
  | "active"
  | "draining"
  | "publishing"
  | "committed"
  | "failed";

/**
 * Attempts spent on one uncertain marker.
 *
 * [D] Six. The marker is a single small POST with no body to re-derive, so the only thing
 * an attempt costs is a round trip; the reason to bound it at all is that a device holding
 * the wrap-up open forever is worse than one that reports honestly. Six with the backoff
 * below is roughly half a minute, which covers a tunnel and a redeploy without turning a
 * dead network into an indefinite spinner.
 */
const DEFAULT_MAX_ATTEMPTS = 6;

/**
 * Lease refreshes spent on one marker.
 *
 * [D] Two, matching the append path. One takeover needs one refresh; needing three means
 * the scope is being contested faster than this device can quote it, and spinning is
 * worse than reporting.
 */
const DEFAULT_MAX_LEASE_REFRESHES = 2;

/**
 * Wall-clock bound on one publish attempt, and on the whole operation.
 *
 * [D] 8 s per attempt, 30 s overall. The attempt bound is what makes an unresolved
 * promise finite -- a mobile radio that has lost the network without noticing produces a
 * `fetch` that never settles, and no attempt *count* helps, because an attempt that never
 * ends is never counted. The overall bound exists because attempts multiply: six attempts
 * at 8 s plus backoff is over a minute of somebody watching a wrap-up screen, which is
 * its own failure even though every individual step is bounded.
 *
 * Both are deliberately generous relative to the request itself -- the marker is a small
 * POST against an indexed primary key -- because the cost of giving up early is a session
 * reported incomplete when it would have succeeded.
 */
const DEFAULT_ATTEMPT_DEADLINE_MS = 8_000;
const DEFAULT_TOTAL_DEADLINE_MS = 30_000;

const realSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Run one cancellable network attempt under a wall-clock bound.
 *
 * The abort is the point, not the race: aborting makes the underlying `fetch` reject, so
 * the attempt genuinely ends rather than being abandoned while still holding a socket.
 * The rejection handler is attached *before* the race so a late rejection cannot surface
 * as an unhandled one -- which also covers a caller that ignores the signal, as a test
 * double does.
 *
 * Shared by every claim-delta request that a stop can wait on, because they all have the
 * same failure: a mobile radio that has lost the network without noticing produces a
 * promise that neither resolves nor rejects, and an attempt *count* cannot bound it --
 * counting attempts requires attempts to end.
 */
export function runWithDeadline<T>(
  work: (options: { signal: AbortSignal }) => Promise<T>,
  ms: number,
  describe: string
): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const attempt = work({ signal: controller.signal });
  attempt.catch(() => {});
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(
        Object.assign(
          new Error(`${describe} did not answer within ${Math.round(ms / 1000)}s`),
          { timedOut: true }
        )
      );
    }, ms);
  });
  return Promise.race([attempt, deadline]).finally(() => clearTimeout(timer));
}

/** Exponential with a ceiling, so a long outage does not become a long silence. */
function backoffMs(attempt: number): number {
  return Math.min(400 * 2 ** (attempt - 1), 5000);
}

/**
 * Owns one recording's claim-delta finality.
 *
 * Single-instance per recording, and every method is safe to call twice: a Stop press is
 * a thing people do repeatedly when the first one reports a problem, and a second press
 * must join the in-flight marker rather than starting a second one.
 */
export class FinalWatermarkGate {
  private activated: ActivateBody | null = null;
  private activation: Promise<ActivationOutcome> | null = null;
  /**
   * Whether the activation row is *known* to exist, as opposed to assumed.
   *
   * False after an uncertain activation, which is not the same as inactive: the device
   * proceeds in delta mode either way, and this only decides whether a
   * `device_state_missing` marker refusal is worth one recovery attempt.
   */
  private activationCertain = false;
  /** The immutable pair, frozen at the moment the drain completed. */
  private ack: Readonly<WriterAck> | null = null;
  private committed: { outcome: MarkerOutcome; body: FinalWatermarkBody } | null = null;
  private inFlight: Promise<MarkerOutcome> | null = null;
  private failure: MarkerTerminalError | null = null;
  private readonly maxAttempts: number;
  private readonly maxLeaseRefreshes: number;
  private readonly attemptDeadlineMs: number;
  private readonly totalDeadlineMs: number;
  private readonly now: () => number;

  constructor(private readonly ports: FinalWatermarkPorts) {
    this.maxAttempts = ports.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
    this.maxLeaseRefreshes = ports.maxLeaseRefreshes ?? DEFAULT_MAX_LEASE_REFRESHES;
    this.attemptDeadlineMs = ports.attemptDeadlineMs ?? DEFAULT_ATTEMPT_DEADLINE_MS;
    this.totalDeadlineMs = ports.totalDeadlineMs ?? DEFAULT_TOTAL_DEADLINE_MS;
    this.now = ports.now ?? (() => Date.now());
  }

  /** One publish attempt, bounded and cancellable. */
  private publishAttempt(body: FinalWatermarkBody): Promise<MarkerResponse> {
    return runWithDeadline(
      (options) => this.ports.publish(body, options),
      this.attemptDeadlineMs,
      "the final watermark"
    );
  }

  /** One activation attempt, bounded the same way and for the same reason. */
  private activateAttempt(body: ActivateBody): Promise<MarkerResponse> {
    return runWithDeadline(
      (options) => this.ports.activate(body, options),
      this.attemptDeadlineMs,
      "the claim-delta activation"
    );
  }

  /** Whether this device holds a durable activation, and is therefore barrier-counted. */
  get isActive(): boolean {
    return this.activated !== null;
  }

  /** Whether that activation was confirmed, rather than assumed after an uncertain answer. */
  get isActivationCertain(): boolean {
    return this.activationCertain;
  }

  get isCommitted(): boolean {
    return this.committed !== null;
  }

  get outcome(): MarkerOutcome | null {
    return this.committed?.outcome ?? null;
  }

  get terminalReason(): MarkerTerminalReason | null {
    return this.failure?.reason ?? null;
  }

  /**
   * The body actually sent, for tests and telemetry. Frozen once published.
   *
   * Exposed so a test can assert the pair did not change across retries -- which is the
   * property this whole class exists to hold, and one that is invisible from the outside
   * otherwise.
   */
  get publishedBody(): FinalWatermarkBody | null {
    return this.committed?.body ?? null;
  }

  /**
   * Record that this device is entering delta mode. Idempotent, and once only.
   *
   * Returns whether delta mode is active. `false` means the session may use the legacy
   * path *for this whole recording* -- but only because activation never succeeded. After
   * a durable activation there is no downgrade, which is why the answer is cached rather
   * than recomputed: a later transient failure must not look like permission to fall back.
   */
  activateOnce(context: ClaimDeltaContext): Promise<ActivationOutcome> {
    if (this.activated) return Promise.resolve("active");
    if (!this.activation) {
      // A thrown activation is `uncertain`, never `declined`: an exception here says
      // nothing about whether the row was written, and `declined` is a claim that it was
      // not.
      this.activation = this.runActivation(context).catch(() => "uncertain" as const);
    }
    return this.activation;
  }

  private async runActivation(context: ClaimDeltaContext): Promise<ActivationOutcome> {
    const body: ActivateBody = {
      timeline_epoch: this.ports.timelineEpoch(),
      producer_id: context.producer_id,
      fence: context.fence,
      session_generation: context.session_generation,
    };
    this.ports.onStage?.("activating");
    const sleep = this.ports.sleep ?? realSleep;
    const startedAt = this.now();

    for (let attempt = 1; attempt <= this.maxAttempts; attempt += 1) {
      // Bounded overall as well as per attempt. A recording must reach *some* protocol
      // decision promptly: spending the opening of the conversation deciding is the same
      // loss as choosing legacy, with a spinner on top. Uncertain rather than declined,
      // because running out of time proves nothing about whether the row was written.
      if (this.now() - startedAt >= this.totalDeadlineMs) return this.assumeActivated(body);
      try {
        const response = await this.activateAttempt(body);
        const status = response?.data?.status;
        if (status !== "accepted" && status !== "duplicate") {
          // A 2xx whose body is not an activation. The request *was* answered, so nothing
          // is in doubt: the server did not activate this device and nothing durable
          // exists.
          return "declined";
        }
        this.activated = body;
        this.activationCertain = true;
        this.ports.onStage?.("active", status);
        return "active";
      } catch (raw) {
        const error = raw as Error & { status?: number };
        const verdict = classifyMarkerError(error);
        if (verdict === "signed-out") {
          this.ports.onSignedOut?.(error);
          // A dead token cannot have written a row under this device's identity.
          return "declined";
        }
        if (verdict !== "retry") {
          // A *definitive* refusal, reached before anything durable could exist: the
          // deployment does not offer the route, or this device has no accepted producer
          // yet. This is the one branch where the legacy path is a genuine compatibility
          // choice rather than a guess about a row that may exist.
          return "declined";
        }
        // Every remaining attempt is a read-back as much as a retry: the route is
        // idempotent, so a later success answers `duplicate` and resolves the ambiguity
        // outright. Only exhausting them leaves it unresolved.
        if (attempt === this.maxAttempts) return this.assumeActivated(body);
        await sleep(backoffMs(attempt));
      }
    }
    return this.assumeActivated(body);
  }

  /**
   * Proceed as activated after an uncertain answer, rather than declaring inactive.
   *
   * The asymmetry is the whole fix. Assuming *activated* when the row does not exist
   * costs a session its claim finality and says so: the final marker is refused
   * `device_state_missing`, this device reports nothing, and the server degrades the
   * session explicitly at its own deadline. Assuming *inactive* when the row does exist
   * is silent and worse: the browser sends legacy bundles that cannot discharge the epoch
   * it owes, the merge waits for a marker that will never arrive, and every party
   * involved believes the session finished cleanly.
   *
   * It is also usually self-correcting. The append path creates the state row on the
   * first accepted delta, so a device that goes on to write anything at all repairs the
   * missing row without knowing it had one.
   */
  private assumeActivated(body: ActivateBody): ActivationOutcome {
    this.activated = body;
    this.activationCertain = false;
    this.ports.onStage?.("active", "uncertain");
    return "uncertain";
  }

  /**
   * Freeze the pair to publish. Call once, immediately after a successful drain.
   *
   * Refuses a `null` ack rather than substituting a zero pair: `null` means the outbox is
   * not settled, and `(0, null)` is a real claim -- that this device produced no deltas at
   * all. Confusing the two would publish "I sent nothing" for a device holding a queue.
   */
  sealAck(ack: Readonly<WriterAck> | null): void {
    if (this.ack) return;
    if (ack === null) {
      throw new MarkerTerminalError(
        "drain_incomplete",
        "the claim-delta writer had not settled when the final watermark was sealed; " +
          "no marker may be published from an unacknowledged queue"
      );
    }
    this.ack = Object.freeze({ ...ack });
  }

  get sealedAck(): Readonly<WriterAck> | null {
    return this.ack;
  }

  /**
   * Publish the marker. Shared by concurrent callers; sent at most once successfully.
   *
   * Throws `MarkerTerminalError` when the marker can never be published. The caller must
   * not proceed to leave/finalise on that path, and must not fall back to a legacy claim.
   */
  publishOnce(): Promise<MarkerOutcome> {
    if (this.committed) return Promise.resolve(this.committed.outcome);
    if (this.failure) return Promise.reject(this.failure);
    // One logical operation: a second Stop press joins this promise rather than opening a
    // second marker request, so the server never sees two concurrent markers from one
    // device and the caller never gets two answers to reconcile.
    if (!this.inFlight) {
      this.inFlight = this.runPublish().finally(() => {
        this.inFlight = null;
      });
    }
    return this.inFlight;
  }

  private async runPublish(): Promise<MarkerOutcome> {
    if (!this.activated) {
      throw this.fail(
        new MarkerTerminalError(
          "device_state_missing",
          "no durable claim-delta activation for this recording, so there is no epoch to " +
            "finalise"
        )
      );
    }
    if (!this.ack) {
      throw this.fail(
        new MarkerTerminalError(
          "drain_incomplete",
          "the final writer ACK was never sealed; the queue was not drained"
        )
      );
    }

    // Built once, here, and from the sealed pair only. Every later attempt sends this
    // object with at most its three lease fields replaced.
    let body: FinalWatermarkBody = Object.freeze({
      timeline_epoch: this.activated.timeline_epoch,
      final_delta_sequence: this.ack.sequence,
      final_digest: this.ack.digest,
      producer_id: this.activated.producer_id,
      producer_fence: this.activated.fence,
      producer_generation: this.activated.session_generation,
    });

    const sleep = this.ports.sleep ?? realSleep;
    let leaseRefreshes = 0;
    let repaired = false;
    const startedAt = this.now();
    this.ports.onStage?.("publishing");

    for (let attempt = 1; attempt <= this.maxAttempts; attempt += 1) {
      // Checked before each attempt, not only after. Attempts multiply, and a bound that
      // is only tested on the way out lets the last one run past it.
      if (this.now() - startedAt >= this.totalDeadlineMs) {
        throw this.fail(
          new MarkerTerminalError(
            "marker_timeout",
            `the final watermark did not complete within ${Math.round(
              this.totalDeadlineMs / 1000
            )}s; this device's claims are not reported as complete`
          )
        );
      }
      try {
        const response = await this.publishAttempt(body);
        const status = response?.data?.status;
        const outcome: MarkerOutcome = status === "duplicate" ? "duplicate" : "accepted";
        if (status !== "accepted" && status !== "duplicate") {
          throw this.fail(
            new MarkerTerminalError(
              "marker_refused",
              `the final-watermark route answered ${response?.status ?? "?"} with an ` +
                `unrecognised status ${String(status)}`
            )
          );
        }
        this.committed = { outcome, body };
        this.ports.onStage?.("committed", outcome);
        return outcome;
      } catch (raw) {
        if (raw instanceof MarkerTerminalError) throw raw;
        const error = raw as Error & { status?: number; data?: unknown };
        const verdict = classifyMarkerError(error);

        if (verdict === "signed-out") {
          this.ports.onSignedOut?.(error);
          throw this.fail(
            new MarkerTerminalError(
              "session_closed",
              `this device is signed out and cannot publish its final watermark ` +
                `(${error.message})`
            )
          );
        }

        if (verdict === "terminal") {
          const reason = (reasonOf(error) ?? "marker_refused") as MarkerTerminalReason;
          // The one refusal an *assumed* activation can repair. If the row genuinely never
          // committed, creating it now and re-publishing turns a session that would have
          // degraded into one that finishes correctly -- and it is safe because activation
          // is idempotent, so this is a no-op if the row was there all along. Tried once,
          // and only when the activation was never confirmed.
          if (reason === "device_state_missing" && !this.activationCertain && !repaired) {
            repaired = true;
            const outcome = await this.runActivation({
              producer_id: body.producer_id,
              fence: body.producer_fence,
              session_generation: body.producer_generation,
            });
            if (outcome === "active") continue;
          }
          throw this.fail(
            new MarkerTerminalError(
              TERMINAL_REASONS.has(reason) ? reason : "marker_refused",
              `the server refused this device's final watermark (${error.message}); the ` +
                "recording's claims cannot be reported as complete"
            )
          );
        }

        if (verdict === "refresh-lease") {
          leaseRefreshes += 1;
          if (leaseRefreshes > this.maxLeaseRefreshes) {
            throw this.fail(
              new MarkerTerminalError(
                "producer_lease_exhausted",
                `the producer lease was refused ${leaseRefreshes} times while publishing ` +
                  `the final watermark (${error.message})`
              )
            );
          }
          const refreshed = await this.refreshLease();
          if (refreshed === null) {
            throw this.fail(
              new MarkerTerminalError(
                "producer_lease_exhausted",
                "the producer lease could not be re-read, so the final watermark cannot " +
                  "be re-wrapped"
              )
            );
          }
          // The pair is copied across untouched. This is the only mutation the body ever
          // undergoes, and it is why the three lease fields are separate from it.
          body = Object.freeze({
            ...body,
            producer_id: refreshed.producer_id,
            producer_fence: refreshed.fence,
            producer_generation: refreshed.session_generation,
          });
          continue;
        }

        // `retry`: the answer is unknown, so the identical body goes again. The route is
        // idempotent on its primary key and checks the exact pair before any producer or
        // session rejection, so a resend of a request that already committed comes back
        // `duplicate` -- including after a takeover.
        //
        // A timed-out attempt lands here too, and that is deliberate: a deadline says
        // nothing about whether the write committed, so it is exactly the uncertainty an
        // exact resend is built to resolve. What the deadline changes is that the
        // uncertainty is now *finite*.
        const timedOut = (error as { timedOut?: boolean }).timedOut === true;
        if (attempt === this.maxAttempts) {
          throw this.fail(
            new MarkerTerminalError(
              timedOut ? "marker_timeout" : "marker_refused",
              `the final watermark could not be published after ${attempt} attempts ` +
                `(${error.message}); this device's claims are not reported as complete`
            )
          );
        }
        await sleep(backoffMs(attempt));
      }
    }

    throw this.fail(
      new MarkerTerminalError("marker_refused", "the final watermark was never published")
    );
  }

  private async refreshLease(): Promise<ClaimDeltaContext | null> {
    try {
      return await this.ports.context();
    } catch {
      return null;
    }
  }

  private fail(error: MarkerTerminalError): MarkerTerminalError {
    this.failure ??= error;
    this.ports.onStage?.("failed", this.failure.reason);
    return this.failure;
  }
}
